'use client';

import { useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useIsClientProfileForm } from '@/context/ClientProfileFormProvider';
import { ModalTypes, useSystemModals } from '@/context/SystemModalsProvider';
import type { ErrorResponse } from '@/types/apiTypes';
import { sendPreRequestAuditIfMatched } from '@/utils/audit/sendPreRequestAudit';
import {
  buildLoginRedirectPath,
  clientProfileFormSession,
  toShareApiPath,
} from '@/utils/auth/clientProfileFormSession';
import { refreshAccessToken } from '@/utils/auth/refreshAccessToken';
import { getParsedError } from '@/utils/getParsedError';
import { handleMaintenanceHttpError } from '@/utils/maintenance/handleMaintenanceHttpError';
import { showRateLimitErrorToast } from '@/utils/rateLimit/showRateLimitErrorToast';
import { TOKEN_KEY } from '../../consts/const';
import type { ErrorType } from './types/types';
import { coerceToBodyInit, isToasterError } from './utils';

function buildRequestOptions<RequestBody>(
  options: Omit<RequestInit, 'body'> & { body?: RequestBody },
  accessToken: string | null
): RequestInit {
  const { body, ...restOptions } = options;

  const headers: HeadersInit = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> | undefined),
  };

  if (accessToken) {
    (headers as Record<string, string>).Authorization = `Bearer ${accessToken}`;
  }

  return {
    ...restOptions,
    body: body ? coerceToBodyInit(body) : undefined,
    headers,
  };
}

export function useFetch<RequestBody = unknown, ResponseBody = unknown, ErrorFields = unknown>() {
  const { onOpenModal } = useSystemModals();
  const isClientProfileForm = useIsClientProfileForm();
  const [error, setError] = useState<ErrorType | null>(null);
  const onOpenModalRef = useRef(onOpenModal);
  onOpenModalRef.current = onOpenModal;

  const doFetch = useCallback(
    async (
      url: string,
      options: Omit<RequestInit, 'body'> & {
        body?: RequestBody;
        suppressErrorToast?: boolean;
        suppressAccessRestrictedModal?: boolean;
      } = {},
      successCallback?: VoidFunction
    ): Promise<ResponseBody> => {
      const baseUrl = `${process.env.NEXT_PUBLIC_API_URL}`;
      const { suppressErrorToast, suppressAccessRestrictedModal, ...restOptions } = options;

      const executeRequest = async (accessToken: string | null) => {
        const requestOptions = buildRequestOptions<RequestBody>(restOptions, accessToken);
        const requestUrl = isClientProfileForm ? toShareApiPath(url) : url;
        await sendPreRequestAuditIfMatched(requestUrl, requestOptions);
        return fetch(`${baseUrl}${requestUrl}`, requestOptions);
      };

      const accessToken = isClientProfileForm ? clientProfileFormSession.readToken() : localStorage.getItem(TOKEN_KEY);
      let response = await executeRequest(accessToken);

      if (response.status === 401 && isClientProfileForm) {
        clientProfileFormSession.clear();
        window.location.replace(buildLoginRedirectPath(window.location));
      } else if (response.status === 401) {
        const newToken = await refreshAccessToken();
        response = await executeRequest(newToken);
      }

      if (!response.ok) {
        const errorBody: ErrorResponse<ErrorFields> = await response.json();

        if (handleMaintenanceHttpError(response.status, errorBody)) {
          throw {
            ...errorBody,
            status: response.status,
            maintenance: true,
          };
        }

        if (response.status !== 429) {
          setError({
            status: response.status,
            errorMessage: getParsedError(errorBody, response.status),
          });
        }

        if (response.status === 403 && !suppressAccessRestrictedModal && !isClientProfileForm) {
          onOpenModalRef.current(ModalTypes.ACCESS_RESTRICTED);
        }

        if (response.status === 403 && isClientProfileForm && !suppressErrorToast) {
          toast.error(getParsedError(errorBody, response.status), { toastId: response.status });
        }

        if (response.status === 410 && isClientProfileForm && !suppressErrorToast) {
          toast.error(errorBody.message, { toastId: response.status });
        }

        if (response.status === 429 && !suppressErrorToast) {
          showRateLimitErrorToast(errorBody);
        }

        if (
          response.status !== 401 &&
          response.status !== 429 &&
          response.status !== 503 &&
          isToasterError(errorBody) &&
          !suppressErrorToast
        ) {
          toast.error(getParsedError(errorBody.errors.details, response.status));
        }
        if (response.status === 404 && !suppressErrorToast) {
          toast.error(getParsedError(errorBody, response.status));
        }
        if (response.status === 500 && !suppressErrorToast) {
          toast.error(getParsedError(errorBody, response.status));
        }

        throw {
          ...errorBody,
          status: response.status,
        };
      }

      successCallback?.();

      if (response.status === 204) {
        return null as unknown as ResponseBody;
      }

      return response.json() as Promise<ResponseBody>;
    },
    [isClientProfileForm]
  );

  return {
    doFetch,
    error,
    setError,
  };
}
